import { Bookmark, Coins, Filter, Hash, ListChecks, Search, SlidersHorizontal, Store, Tag, Trash2, X } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useTRPC } from '@/api/query';
import { useDates } from '@/lib/dates';
import {
  Badge, Button, DateInput, Field, IconButton, Input, MoneyInput, Popover, PopoverContent, PopoverTrigger, Select, Sheet, SheetContent, toast,
} from '@/components/ui';
import { cn } from '@/lib/cn';
import { PHONE, useMediaQuery } from '@/lib/media';
import { categoryTree, useLedger, withDescendants } from './data';
import { DATE_PRESET_OPTIONS, type DatePreset } from './date-presets';
import { activeFilterCount, EMPTY_FILTER, useSavedFilters, type TxFilter } from './filters';
import { STATUS_OPTIONS, type TxStatus } from './labels';
import { MultiSelect } from './MultiSelect';

export interface TxFilterBarProps {
  readonly filter: TxFilter;
  readonly onChange: (f: TxFilter) => void;
  /** Register is locked to this account (hide the account picker). */
  readonly lockedAccountId?: string;
  /** Show saved filters (off when embedded). */
  readonly saved?: boolean;
  /** Unreviewed count for the "To review" toggle. */
  readonly unreviewedCount?: number;
}

const ALL = '__all';
const STMT = 'stmt:';

/** Register filters: search, date preset (FY-aware), account, category tree, payees, tags, status, amount, review state, saved filters. */
export const TxFilterBar = ({ filter, onChange, lockedAccountId, saved = true, unreviewedCount }: TxFilterBarProps) => {
  const { entityId, accounts, categories, payees, tags } = useLedger();
  const [q, setQ] = useState(filter.q);
  const set = (patch: Partial<TxFilter>) => onChange({ ...filter, ...patch });

  useEffect(() => setQ(filter.q), [filter.q]);
  useEffect(() => {
    if (q === filter.q) return;
    const t = setTimeout(() => onChange({ ...filter, q }), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const catOptions = useMemo(
    () => categoryTree(categories).map((n) => ({ value: n.category.id, label: n.category.name, depth: n.depth, keywords: [n.path] })),
    [categories],
  );
  const count = activeFilterCount(filter);

  // one account in view: its imported statement periods are date ranges too
  const trpc = useTRPC();
  const dates = useDates();
  const statementAccount = lockedAccountId ?? filter.accountId;
  const statements = useQuery(trpc.importQueue.statements.queryOptions({ accountId: statementAccount ?? '' }, { enabled: !!statementAccount, meta: { silent: true } }));
  const statementOptions = (statements.data ?? []).filter((st) => st.periodStart).reverse().map((st) => ({
    value: `${STMT}${st.periodStart}|${st.periodEnd}`, label: `Statement ${dates.format(st.periodStart!, 'short')} – ${dates.format(st.periodEnd, 'short')}`,
  }));
  const statementValue = filter.preset === 'custom' ? statementOptions.find((o) => o.value === `${STMT}${filter.from}|${filter.to}`)?.value : undefined;

  // phones: one "Filters" button opening a sheet, so the list isn't pushed below the fold
  const narrow = useMediaQuery(PHONE);
  const [sheet, setSheet] = useState(false);
  const filters = (
    <>
      <Select
        aria-label="Date range"
        value={statementValue ?? filter.preset}
        onValueChange={(v) => {
          const [from, to] = v.startsWith(STMT) ? v.slice(STMT.length).split('|') : [];
          set(from && to ? { preset: 'custom', from, to } : { preset: v as DatePreset });
        }}
        options={[...DATE_PRESET_OPTIONS, ...statementOptions]}
        className="h-9 w-auto min-w-[10rem] coarse:h-11"
      />
      {filter.preset === 'custom' ? (
        <div className="flex items-center gap-1">
          <DateInput aria-label="From date" value={filter.from ?? ''} onChange={(e) => set({ from: e.target.value || undefined })} className="h-9 w-40 coarse:h-11" />
          <span className="text-sm text-text-muted">to</span>
          <DateInput aria-label="To date" value={filter.to ?? ''} onChange={(e) => set({ to: e.target.value || undefined })} className="h-9 w-40 coarse:h-11" />
        </div>
      ) : null}
      {lockedAccountId ? null : (
        <Select
          aria-label="Account"
          value={filter.accountId ?? ALL}
          onValueChange={(v) => set({ accountId: v === ALL ? undefined : v })}
          options={[{ value: ALL, label: 'All accounts' }, ...accounts.map((a) => ({ value: a.id, label: a.isClosed ? `${a.name} (closed)` : a.name }))]}
          className="h-9 w-auto min-w-[9rem] coarse:h-11"
        />
      )}
      <MultiSelect
        label="Categories"
        icon={<Tag aria-hidden />}
        options={catOptions}
        value={filter.categoryIds}
        onChange={(categoryIds) => set({ categoryIds })}
        expand={(id) => withDescendants(categories, [id])}
      />
      <MultiSelect
        label="Payees"
        icon={<Store aria-hidden />}
        options={payees.map((p) => ({ value: p.id, label: p.name, keywords: p.aliases }))}
        value={filter.payeeIds}
        onChange={(payeeIds) => set({ payeeIds })}
      />
      {tags.length ? (
        <MultiSelect
          label="Tags"
          icon={<Hash aria-hidden />}
          options={tags.map((t) => ({ value: t.id, label: t.name }))}
          value={filter.tagIds}
          onChange={(tagIds) => set({ tagIds })}
        />
      ) : null}
      <MultiSelect
        label="Status"
        icon={<ListChecks aria-hidden />}
        options={STATUS_OPTIONS}
        value={filter.status}
        onChange={(status) => set({ status: status as TxStatus[] })}
      />
      <AmountFilter filter={filter} onChange={onChange} />
    </>
  );
  const extras = (
    <>
      {saved && entityId ? <SavedFilters entityId={entityId} filter={filter} onApply={onChange} /> : null}
      {count ? (
        <Button
          variant="ghost"
          size="sm"
          icon={<X aria-hidden />}
          onClick={() => onChange({ ...EMPTY_FILTER, accountId: filter.accountId })}
        >
          Clear filters
        </Button>
      ) : null}
    </>
  );

  return (
    <div className="flex flex-col gap-2" data-tour="register-filters">
      <div className="flex flex-wrap items-center gap-2">
        <Input
          type="search"
          aria-label="Search transactions"
          placeholder="Search payee, memo, reference"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          start={<Search aria-hidden />}
          className="h-9 w-full sm:w-64 coarse:h-11"
        />
        {narrow ? (
          <>
            <Button variant="outline" size="sm" icon={<SlidersHorizontal aria-hidden />} onClick={() => setSheet(true)}>
              Filters{count ? <Badge tone="accent">{count}</Badge> : null}
            </Button>
            <Button
              variant={filter.unreviewedOnly ? 'secondary' : 'outline'}
              size="sm"
              aria-pressed={filter.unreviewedOnly}
              onClick={() => set({ unreviewedOnly: !filter.unreviewedOnly })}
            >
              To review
              {unreviewedCount ? <Badge tone={filter.unreviewedOnly ? 'solid' : 'accent'}>{unreviewedCount}</Badge> : null}
            </Button>
            <Sheet open={sheet} onOpenChange={setSheet}>
              <SheetContent title="Filters" description="Narrow down the register." footer={<Button className="w-full" onClick={() => setSheet(false)}>Show transactions</Button>}>
                <div className="flex flex-col items-stretch gap-3 [&>*]:w-full">
                  {filters}
                  {extras}
                </div>
              </SheetContent>
            </Sheet>
          </>
        ) : (
          <>
            {filters}
            <Button
              variant={filter.unreviewedOnly ? 'secondary' : 'outline'}
              size="sm"
              aria-pressed={filter.unreviewedOnly}
              onClick={() => set({ unreviewedOnly: !filter.unreviewedOnly })}
            >
              To review
              {unreviewedCount ? <Badge tone={filter.unreviewedOnly ? 'solid' : 'accent'}>{unreviewedCount}</Badge> : null}
            </Button>
            {extras}
          </>
        )}
      </div>
    </div>
  );
};

const AmountFilter = ({ filter, onChange }: { readonly filter: TxFilter; readonly onChange: (f: TxFilter) => void }) => {
  const active = filter.minCents !== undefined || filter.maxCents !== undefined;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'inline-flex h-9 items-center gap-1.5 rounded-md border px-3 text-sm coarse:h-11 [&_svg]:size-4',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            active ? 'border-primary/50 bg-primary-soft text-text' : 'border-border-strong/60 bg-surface text-text-muted hover:text-text',
          )}
        >
          <Coins aria-hidden /> Amount
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72">
        <p className="mb-3 text-xs text-text-muted">Matches money in or out of this size.</p>
        <div className="grid grid-cols-2 gap-3">
          <Field label="At least">
            <MoneyInput value={filter.minCents ?? null} allowNegative={false} commitOnBlur onChange={(c) => onChange({ ...filter, minCents: c ?? undefined })} />
          </Field>
          <Field label="At most">
            <MoneyInput value={filter.maxCents ?? null} allowNegative={false} commitOnBlur onChange={(c) => onChange({ ...filter, maxCents: c ?? undefined })} />
          </Field>
        </div>
        {active ? (
          <Button variant="ghost" size="sm" className="mt-2" onClick={() => onChange({ ...filter, minCents: undefined, maxCents: undefined })}>
            Clear amount
          </Button>
        ) : null}
      </PopoverContent>
    </Popover>
  );
};

const SavedFilters = ({ entityId, filter, onApply }: { readonly entityId: string; readonly filter: TxFilter; readonly onApply: (f: TxFilter) => void }) => {
  const all = useSavedFilters((s) => s.saved);
  const saveFilter = useSavedFilters((s) => s.save);
  const removeFilter = useSavedFilters((s) => s.remove);
  const mine = all.filter((s) => s.entityId === entityId);
  const [name, setName] = useState('');
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" icon={<Bookmark aria-hidden />}>
          Saved
          {mine.length ? <Badge>{mine.length}</Badge> : null}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80" align="end">
        <p className="mb-2 font-medium">Saved filters</p>
        {mine.length ? (
          <ul className="mb-3 flex flex-col gap-1">
            {mine.map((s) => (
              <li key={s.id} className="flex items-center gap-1">
                <button
                  type="button"
                  className="flex min-h-10 flex-1 items-center gap-2 rounded-md px-2 text-left text-sm hover:bg-surface-sunken"
                  onClick={() => {
                    onApply({ ...EMPTY_FILTER, ...s.filter });
                    setOpen(false);
                  }}
                >
                  <Filter className="size-4 text-text-muted" aria-hidden />
                  {s.name}
                </button>
                <IconButton aria-label={`Delete saved filter ${s.name}`} icon={<Trash2 aria-hidden />} onClick={() => removeFilter(s.id)} />
              </li>
            ))}
          </ul>
        ) : (
          <p className="mb-3 text-sm text-text-muted">Save the filters you use often, like “Tax deductible this FY”.</p>
        )}
        <form
          className="flex items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (!name.trim()) return;
            saveFilter(entityId, name.trim(), filter);
            toast.success(`Saved “${name.trim()}”`);
            setName('');
          }}
        >
          <Field label="Save current filters as" className="flex-1">
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" />
          </Field>
          <Button type="submit" disabled={!name.trim() || activeFilterCount(filter) === 0}>
            Save
          </Button>
        </form>
      </PopoverContent>
    </Popover>
  );
};
